import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { FC, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { GetPurchaseOrderResponse } from "../../../../../../api/services/client/models";
import {
  useGetClientBillingDetailsByClientId,
  useGetCurrencies,
  useGetProgramFeeDetails,
} from "../../../../../../api/services/program/programApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiButtonTertiary,
} from "../../../../../../components";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import { TaxDetails } from "../../../../../../components/taxDetails/TaxDetails";
import {
  COUNTRY_CURRENCY_MAP,
  CURRENCY_FORMAT_MAP,
  CurrencyFormat,
} from "../../../../../../components/textFieldCurrency/CurrencyUtils";
import {
  FORM_ERROR_TIMEOUT_LONG,
  MODULE_PERMISSIONS,
  TIMEOUT_300,
} from "../../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../../helpers/constants/componentIds";
import { commonSiteCoreKeys } from "../../../../../../helpers/constants/siteCoreKeys";
import {
  getFormattedAmount,
  getIdByNameFromList,
  getNameByIdFromList,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../../../helpers/utils";
import useAccordion from "../../../../../../hooks/useAccordion";
import { useForceUpdate } from "../../../../../../hooks/useForceUpdate";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import {
  billingConfigurationDetailsFormKeys,
  ProgramDetail,
  ProgramFeeSectionKey,
  ProgramFeeTabFormKey,
  taxDetailsFormKeys,
} from "../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import FeeDetails from "./components/feeDetails/FeeDetails";

interface ProgramFeeTabProps {
  pageId: string;
  programData?: ProgramDetail;
  programId: string;
  isEditProgram?: boolean;
  refetchProgramFee: boolean;
  purchaseOrderData?: GetPurchaseOrderResponse;
  isRefetch?: boolean;
}

const initialAccordionStatus = {
  feeDetails: true,
  taxDetails: false,
};

export const ProgramFeeTab: FC<ProgramFeeTabProps> = ({
  pageId,
  programData,
  isEditProgram = false,
  programId,
  refetchProgramFee,
  purchaseOrderData,
  isRefetch,
}) => {
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
    handleAccordionExpansionState,
  } = useAccordion(initialAccordionStatus);

  const [defaultUserCurrencyId, setDefaultUserCurrencyId] = useState("");
  const forceUpdate = useForceUpdate();
  const currentTemplateId = useRef<string>();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const isBillingConfigurationExists =
    !!programData?.isBillingConfigurationExists;

  const dispatch = useDispatch();

  const {
    getValues,
    reset,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [programFeeForTax, setProgramFeeForTax] = useState<string>("");
  const [resetData, setResetData] = useState<boolean>(false);

  const { hasModulePermission } = usePermissions();

  const taxDetailsRef = useRef<{
    resetData: () => void;
  }>(null);

  const {
    data: programFeeDetailsData,
    isFetching: isProgramFeeDetailsPending,
    isError: isProgramFeeDetailsError,
    isSuccess: isProgramFeeDetailsDataSuccess,
    refetch: refetchProgramFeeDetailsData,
    refetchWithParams: getProgramFeeDetails,
  } = useGetProgramFeeDetails();

  const {
    data: clientBillingDetails,
    isFetching: isClientBillingDetailsPending,
    isError: isClientBillingDetailsError,
    isSuccess: isClientBillingDetailsGetApiSuccess,
    refetchWithParams: getClientBillingDetails,
    resetParams,
  } = useGetClientBillingDetailsByClientId();

  const { fetchComponentTitle, commonLabels, fetchLabel } = useSiteCore();

  const { data: currenciesList, isSuccess: isCurrenciesListSuccess } =
    useGetCurrencies();

  useEffect(() => {
    if (isRefetch) getProgramFeeDetails(programId);
  }, [isRefetch, programId]);

  const resetFormValueExceptCurrency = () => {
    const currencyId = getValues(ProgramFeeTabFormKey.currencyId);
    reset();
    setValue(ProgramFeeTabFormKey.currencyId, currencyId);
  };

  // refetch details when program template change
  useEffect(() => {
    const programTemplateId = programData?.programTemplateId;
    const isFetchClientBillingConfig: boolean =
      isProgramFeeDetailsDataSuccess &&
      programFeeDetailsData === null &&
      isBillingConfigurationExists &&
      getValueOrBlank(currentTemplateId.current) !== programTemplateId;

    if (isFetchClientBillingConfig) {
      const clientId = programData?.clientId;
      currentTemplateId.current = programTemplateId;
      setResetData(!resetData);
      getClientBillingDetails(
        getValueOrBlank(clientId),
        getValueOrBlank(programTemplateId),
      );
    } else if (programFeeDetailsData === null) {
      resetFormValueExceptCurrency();
      setResetData(!resetData);
      resetParams();
    }
  }, [programData?.programTemplateId, isProgramFeeDetailsDataSuccess]);

  useEffect(() => {
    if (!isProgramFeeDetailsPending) {
      refetchProgramFeeDetailsData();
    }
  }, [refetchProgramFee]);

  useEffect(() => {
    if (!isCurrenciesListSuccess) return;

    const setCurrency = (currencyId: string) => {
      setDefaultUserCurrencyId(currencyId);
      setValue(ProgramFeeTabFormKey.currencyId, currencyId);
    };

    if (purchaseOrderData?.currencyTypeId) {
      setCurrency(purchaseOrderData.currencyTypeId);
    } else {
      const countryCode = loggedInUser?.countryCode?.toUpperCase();
      if (countryCode) {
        const currencyName =
          COUNTRY_CURRENCY_MAP[
            countryCode as keyof typeof COUNTRY_CURRENCY_MAP
          ];
        const currencyId = getIdByNameFromList(
          currenciesList ?? [],
          currencyName,
        );
        if (currencyId) setCurrency(currencyId);
      }
    }
  }, [
    purchaseOrderData?.currencyTypeId,
    isCurrenciesListSuccess,
    loggedInUser?.countryCode,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isProgramFeeDetailsPending || isClientBillingDetailsPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isProgramFeeDetailsPending, isClientBillingDetailsPending]);

  useEffect(() => {
    if (isProgramFeeDetailsError || isClientBillingDetailsError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isProgramFeeDetailsError, isClientBillingDetailsError]);

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={labelKey} />;
  };

  const getRemoveIcon = () => {
    return <RemoveIcon color={"primary"} />;
  };

  const getAddIcon = () => {
    return <AddIcon color={"primary"} />;
  };

  const programFeeSectionFieldsMap: Record<ProgramFeeSectionKey, string[]> = {
    feeDetails: [
      ProgramFeeTabFormKey.billingTypeId,
      ProgramFeeTabFormKey.billingTriggerId,
      ProgramFeeTabFormKey.earlyPayDays,
      ProgramFeeTabFormKey.earlyPayPercentage,
      ProgramFeeTabFormKey.minimumTransferFee,
      ProgramFeeTabFormKey.proBonoCategoryId,
      ProgramFeeTabFormKey.currencyId,
      ProgramFeeTabFormKey.fee,
      ProgramFeeTabFormKey.feeWithFormat,
      ProgramFeeTabFormKey.schedulePeriod,
      ProgramFeeTabFormKey.feePaymentType,
      ProgramFeeTabFormKey.schedules,
      ProgramFeeTabFormKey.milestones,
      ProgramFeeTabFormKey.activities,
      ProgramFeeTabFormKey.feePercentage,
      ProgramFeeTabFormKey.amount,
      ProgramFeeTabFormKey.amountWithFormat,
      ProgramFeeTabFormKey.scheduleFrequency,
      ProgramFeeTabFormKey.billingSchedules,
      ProgramFeeTabFormKey.description,
    ],
    taxDetails: [
      ProgramFeeTabFormKey.billingTypeId,
      ProgramFeeTabFormKey.billingTriggerId,
      ProgramFeeTabFormKey.currencyId,
      ProgramFeeTabFormKey.fee,
    ],
  };

  const hasErrors = (section: ProgramFeeSectionKey) => {
    const fields = programFeeSectionFieldsMap[section];
    return fields.some((field) => errors[field]);
  };

  useEffect(() => {
    (Object.keys(programFeeSectionFieldsMap) as ProgramFeeSectionKey[]).forEach(
      (sectionKey: ProgramFeeSectionKey) => {
        if (hasErrors(sectionKey)) {
          handleAccordionExpansionState(sectionKey.toString(), true);
        }
      },
    );
  }, [errors]);

  useEffect(() => {
    if (
      isNotEmpty(clientBillingDetails) &&
      isClientBillingDetailsGetApiSuccess
    ) {
      const {
        billingTypeId,
        billingTriggerId,
        fee,
        billingSchedules,
        currency,
        earlyPayDays,
        earlyPayPercentage,
        feePaymentType,
        schedulePeriod,
        specialBillingInstructions,
      } = clientBillingDetails;
      const feeWithFormat = `${getFormattedAmount(
        fee,
        CURRENCY_FORMAT_MAP[currency?.name]?.locale,
      )}`;
      setValue(ProgramFeeTabFormKey.billingTypeId, billingTypeId);
      billingTriggerId &&
        setValue(ProgramFeeTabFormKey.billingTriggerId, billingTriggerId);
      fee && setValue(ProgramFeeTabFormKey.fee, fee?.toString());
      fee && setValue(ProgramFeeTabFormKey.feeWithFormat, feeWithFormat);
      setValue(ProgramFeeTabFormKey.earlyPayDays, earlyPayDays?.toString());
      setValue(
        ProgramFeeTabFormKey.earlyPayPercentage,
        earlyPayPercentage?.toString(),
      );
      if (!purchaseOrderData?.poNumber)
        setValue(ProgramFeeTabFormKey.currencyId, currency?.id);
      feePaymentType &&
        setValue(ProgramFeeTabFormKey.feePaymentType, feePaymentType);
      schedulePeriod &&
        setValue(ProgramFeeTabFormKey.schedulePeriod, schedulePeriod);
      setValue(ProgramFeeTabFormKey.billingSchedules, billingSchedules);
      setValue(
        ProgramFeeTabFormKey.specialBillingInstructions,
        specialBillingInstructions,
      );
    }
  }, [clientBillingDetails, isClientBillingDetailsGetApiSuccess]);
  useEffect(() => {
    const programTemplateId = programData?.programTemplateId;
    const clientId = programData?.clientId;
    // if getting program fee null then call client billing details
    if (programFeeDetailsData === null && isProgramFeeDetailsDataSuccess) {
      currentTemplateId.current = programTemplateId;
      resetFormValueExceptCurrency();
      setResetData(!resetData);
      isBillingConfigurationExists &&
        getClientBillingDetails(
          getValueOrBlank(clientId),
          getValueOrBlank(programTemplateId),
        );
    }

    if (isNotEmpty(programFeeDetailsData) && isProgramFeeDetailsDataSuccess) {
      const {
        billingTypeId,
        billingTriggerId,
        currency,
        feePaymentType,
        schedulePeriod,
        earlyPayDays,
        earlyPayPercentage,
        minimumTransferFee,
        proBonoCategoryId,
        fee,
        billingSchedules,
        taxes,
        id,
        specialBillingInstructions,
      } = programFeeDetailsData;

      const feeWithFormat = `${getFormattedAmount(
        fee ?? 0,
        CURRENCY_FORMAT_MAP[currency?.name]?.locale,
      )}`;
      const minimumTransferFeeFormat = `${getFormattedAmount(
        minimumTransferFee ?? 0,
        CURRENCY_FORMAT_MAP[currency?.name]?.locale,
      )}`;

      setValue(ProgramFeeTabFormKey.billingTypeId, billingTypeId);

      setTimeout(() => {
        if (!purchaseOrderData?.poNumber)
          setValue(ProgramFeeTabFormKey.currencyId, currency?.id);

        setValue(ProgramFeeTabFormKey.billingTriggerId, billingTriggerId);
        setValue(ProgramFeeTabFormKey.earlyPayDays, earlyPayDays?.toString());
        setValue(
          ProgramFeeTabFormKey.earlyPayPercentage,
          earlyPayPercentage?.toString(),
        );
        minimumTransferFee &&
          setValue(
            ProgramFeeTabFormKey.minimumTransferFee,
            minimumTransferFee?.toString(),
          );
        minimumTransferFee &&
          setValue(
            ProgramFeeTabFormKey.minimumTransferFeeWithFormat,
            minimumTransferFeeFormat,
          );
        setValue(
          ProgramFeeTabFormKey.proBonoCategoryId,
          getValueOrBlank(proBonoCategoryId),
        );
        setValue(ProgramFeeTabFormKey.schedulePeriod, schedulePeriod);
        setValue(ProgramFeeTabFormKey.feePaymentType, feePaymentType);

        setValue(ProgramFeeTabFormKey.id, id);
        setTimeout(() => {
          fee && setValue(ProgramFeeTabFormKey.fee, fee.toString());
          fee && setValue(ProgramFeeTabFormKey.feeWithFormat, feeWithFormat);
          reset(getValues());
        }, TIMEOUT_300);

        setValue(ProgramFeeTabFormKey.billingSchedules, billingSchedules);
        setValue(ProgramFeeTabFormKey.taxEntries, taxes);
        setValue(
          ProgramFeeTabFormKey.specialBillingInstructions,
          specialBillingInstructions,
        );
      }, FORM_ERROR_TIMEOUT_LONG);
    }
  }, [
    programFeeDetailsData,
    isProgramFeeDetailsDataSuccess,
    programData?.programTemplateId,
  ]);

  const { currencyId } = getValues();

  const currencyName = getNameByIdFromList(currenciesList, currencyId);
  const currencyFormat: CurrencyFormat = CURRENCY_FORMAT_MAP[currencyName];

  useEffect(() => {
    setProgramFeeForTax(getValues(ProgramFeeTabFormKey.fee));
  }, [getValues(ProgramFeeTabFormKey.fee)]);

  useEffect(() => {
    taxDetailsRef.current?.resetData();
  }, [resetData]);

  return (
    <MuiBox className="participant-tab">
      <MuiBox className="header-collapse">
        <MuiButtonTertiary
          id={COMPONENT_IDS.EXPAND_COLLAPSE_BUTTON}
          onClick={handleExpandCollapseAllChange}
        >
          {accordionStatus.all
            ? commonLabels.collapseAll
            : commonLabels.expandAll}
          {accordionStatus.all ? getRemoveIcon() : getAddIcon()}
        </MuiButtonTertiary>
      </MuiBox>

      <PermissionWrapper
        permission={
          isEditProgram
            ? MODULE_PERMISSIONS.EDIT_PROGRAM_FEE
            : MODULE_PERMISSIONS.ADD_PROGRAM_FEE
        }
      >
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={renderHeader(
            fetchLabel(
              programSiteCoreKeys.programFeeTitle,
              programSiteCoreKeys.feeDetailsSectionHeading,
            ),
          )}
          onChange={() => handleIndividualAccordionChange("feeDetails")}
          isExpanded={accordionStatus.feeDetails}
        >
          <FeeDetails
            pageId={pageId}
            programData={programData}
            currenciesList={currenciesList || []}
            defaultUserCurrency={defaultUserCurrencyId}
            currencyFormat={currencyFormat}
            currencyName={currencyName}
            forceUpdate={forceUpdate}
            currencyId={currencyId}
            billingConfigurationDetailsFormKeys={
              billingConfigurationDetailsFormKeys
            }
            programFeeDetailsData={programFeeDetailsData}
            resetData={resetData}
            isEditProgram={isEditProgram}
          />
        </AccordionHeader>
      </PermissionWrapper>

      <PermissionWrapper
        permission={
          isEditProgram
            ? MODULE_PERMISSIONS.EDIT_TAX_DETAILS
            : MODULE_PERMISSIONS.ADD_TAX_DETAILS
        }
      >
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={renderHeader(
            fetchComponentTitle(commonSiteCoreKeys.taxDetails),
          )}
          onChange={() => handleIndividualAccordionChange("taxDetails")}
          isExpanded={accordionStatus.taxDetails}
        >
          <TaxDetails
            ref={taxDetailsRef}
            pageId={pageId}
            currencyFormat={currencyFormat}
            currencyName={currencyName}
            forceUpdate={forceUpdate}
            currencyId={currencyId}
            isInEditMode={isEditProgram}
            isInViewMode={
              isNotEmpty(programFeeDetailsData) &&
              !programFeeDetailsData?.istaxEditable
            }
            taxDetailsFormKeys={taxDetailsFormKeys}
            programOrProjectFee={programFeeForTax}
            hasDeletePermission={hasModulePermission(
              MODULE_PERMISSIONS.DELETE_TAX_DETAILS,
            )}
          />
        </AccordionHeader>
      </PermissionWrapper>
    </MuiBox>
  );
};
